
  import { Component, Watch, Vue, Prop }                from 'vue-property-decorator';
  import { Action, Getter }                             from 'vuex-class';
  import { EventBus,
           ConfigService,
           ConfigThemeService,
           EmotionsService,
           EmotionTypeEnum,
           EmotionIdFromString,
           ResponsiveUiHelper }                         from '../..';

           
  @Component({
    name: 'EmotionAvatarComponent',
  })
  export default class EmotionAvatarComponent extends Vue {
    @Prop() isPresentation!: boolean;

    @Action('playEmotion', { namespace: 'conversationSession' }) playEmotion: any;
    @Getter('emotionToPlayId', { namespace: 'conversationSession' }) emotionToPlayId!: string;
    @Getter('isEmbeddedMode', { namespace: 'conversationSession' }) isEmbeddedMode!: boolean | undefined;
    isFullscreenDisplay: boolean = false;

    isEmotionsFeatureActive: boolean = true;
    emotionTypeEnumAsList: string[] = ['presentation', 'neutral', 'think', 'joy', 'sadness'];
    currentEmotionId: string = '';
    
    get avatarUrl() {
      const isBigSize: boolean | undefined = (!this.isFullscreenDisplay && this.isEmbeddedMode);
      if (isBigSize) {
        return ConfigService.getBotImagePresentation();
      }
      return ConfigService.getBotImage();
    }

    get isAnimated() {
      return EmotionsService.areAnimatedEmotionAvatarsConfigured();
    }

    get avatarWidth() {
      if (this.isEmbeddedMode && !this.isFullscreenDisplay) {
        return Number(ConfigThemeService.getPresentationAvatarWidth());
      }
      return 'auto';
    }

    get avatarHeight() {
      let containerHeightValue: number = 0;
      if (this.isEmbeddedMode && !this.isFullscreenDisplay) {
        containerHeightValue = Number(ConfigThemeService.getPresentationAvatarHeight());
      } else {
        containerHeightValue = this.isPresentation  ? Number(ConfigThemeService.getPresentationAvatarHeight())
                                                    : Number(ConfigThemeService.getSmallVersionAvatarWidth());
      }
      containerHeightValue = !isNaN(containerHeightValue) ? containerHeightValue - 8 : 0;
      return isNaN(containerHeightValue) ? '60px' : `${containerHeightValue}px`;
    }

    @Watch('emotionToPlayId')
    onPlayNewEmotion() {
      this.currentEmotionId = this.emotionToPlayId;

      if (EmotionsService.areAnimatedEmotionAvatarsConfigured()) {
        EmotionsService.playEmotionAnimation(this.emotionToPlayId);
      }

      if (!EmotionsService.areAnimatedEmotionAvatarsConfigured() && 
            (this.currentEmotionId === EmotionIdFromString[EmotionTypeEnum.JOY] || 
             this.currentEmotionId === EmotionIdFromString[EmotionTypeEnum.SADNESS])) {
        // play emotion during a short period
        setTimeout(() => this.playEmotion(EmotionTypeEnum.NEUTRAL), ConfigService.getStaticEmotionAvatarPlayTime());
      }
    }

    /** ******************************************************************** */
    /**                      LIFECYCLE HOOKS FUNCTIONS                       */
    /** ******************************************************************** */

    created() {
      this.isEmotionsFeatureActive = EmotionsService.isEmotionFeatureAvailable();
      if (this.isEmotionsFeatureActive) {
        EmotionsService.verifyValidAvatarUrls();

        EventBus.$on('ON_STOP_ANIMATION_EVENT', (emotionId: string) => {
          EmotionsService.stopEmotionAnimation(emotionId);
        });

        EventBus.$on('ON_ANIMATION_LOOP_ENDED_EVENT', () => {
          this.playEmotion(EmotionTypeEnum.NEUTRAL);
        });
      }
      EventBus.$on('ON_RESIZE_SCREEN_MODE', (isFullscreen: boolean) => {
        this.isFullscreenDisplay = isFullscreen;
      });
      this.isFullscreenDisplay = ResponsiveUiHelper.isFullscreenDisplay(this.$vuetify);
    }

    mounted() {
      if (this.isEmotionsFeatureActive && EmotionsService.areAnimatedEmotionAvatarsConfigured()) {
        this.emotionTypeEnumAsList.forEach((emotion: string) => {
          let imgElem: HTMLElement | null = document.getElementById(emotion);
          if (imgElem) {
            const maxWidth: number = this.isPresentation ? Number.parseInt(ConfigThemeService.getPresentationAvatarWidth())
                                                         : Number.parseInt(ConfigThemeService.getSmallVersionAvatarWidth());
            EmotionsService.initAnimatedAvatars(emotion, imgElem, maxWidth);
          }
        });
      }
      
      if (this.isEmotionsFeatureActive) {
        // Display initial emotion
        this.playEmotion(EmotionTypeEnum.PRESENTATION);
      }
    }

    destroyed() {
      EventBus.$off('ON_RESIZE_SCREEN_MODE');
      if (this.isEmotionsFeatureActive) {
        EventBus.$off('ON_ANIMATION_LOOP_ENDED_EVENT');
        EventBus.$off('ON_STOP_ANIMATION_EVENT');
      }
    }

    /** ******************************************************************** */
    /**                    EMOTIONS MANAGEMENT FUNCTIONS                     */
    /** ******************************************************************** */

    getEmotionAvatarId(emotion: string): string {
      return EmotionIdFromString[emotion];
    }

    getAvatarImgAlt(emotion: string): string {
      switch(emotion) {
        case EmotionTypeEnum.PRESENTATION:
          return ConfigService.getAvatarImgAltPresentation();
        case EmotionTypeEnum.THINK:
          return ConfigService.getAvatarImgAltThink();
        case EmotionTypeEnum.JOY:
          return ConfigService.getAvatarImgAltJoy();
        case EmotionTypeEnum.SADNESS:
          return ConfigService.getAvatarImgAltSadness();
        case EmotionTypeEnum.NEUTRAL:
        default:
          return ConfigService.getAvatarImgAltNeutral();
      }
    }

    getEmotionAvatarUrl(emotion: string): string | undefined {
      if (!this.isFullscreenDisplay && this.isEmbeddedMode) {
        return EmotionsService.bigAvatarUrls.get(emotion);
      }
      return EmotionsService.smallAvatarUrls.get(emotion);
    }
  }
